import { ref, onMounted } from 'vue'
import type { Ref } from 'vue'

import { getDocumentPreview } from './api'
import { documentPreviewToDocs } from './document-preview-to-docs'
import type { PdftronDoc } from './types'

interface InlinePreview {
  url?: string
  pdftronPath?: string
}

interface UseDocumentPreview {
  docs: Ref<PdftronDoc[]>
  pdftronPath: Ref<string | undefined>
  loading: Ref<boolean>
  error: Ref<boolean>
  unreadable: Ref<boolean>
}

// Resolves a document's preview for PDFTron. When the server already baked the
// single previewable URL into the HTML (`inline`), it is used as-is — no
// request. Otherwise (a `.msg`, whose tabs require parsing the blob) the
// `#preview` payload is lazy-fetched on mount; a failed fetch sets `error` so
// the viewer can warn rather than sit blank, and an email the server cannot
// read sets `unreadable`.
export function useDocumentPreview(
  documentId: number,
  inline: InlinePreview = {}
): UseDocumentPreview {
  const docs = ref<PdftronDoc[]>(inline.url ? [{ url: inline.url }] : [])
  const pdftronPath = ref<string | undefined>(
    inline.url ? inline.pdftronPath : undefined
  )
  const loading = ref(!inline.url)
  const error = ref(false)
  const unreadable = ref(false)

  async function load(): Promise<void> {
    const response = await getDocumentPreview(documentId)

    if (response) {
      docs.value = documentPreviewToDocs(response)
      pdftronPath.value = response.pdftronPath
      unreadable.value = response.unreadable === true
    } else {
      error.value = true
    }

    loading.value = false
  }

  if (!inline.url) {
    onMounted(load)
  }

  return { docs, pdftronPath, loading, error, unreadable }
}
